import { Box, Grid, Link, Typography } from "@mui/material";
import React, { useContext } from "react";
import { Link as RouterLink } from "react-router-dom";

import { Header, LoginHeader } from "../../components/Header";
import UserContext from "../../context/User";
import useStyle from "../Privacy/styles";
import Seo from "../../components/Seo/Seo";

/* Support page: the App Store "Support URL" for the RushMyFood Restaurant app,
   and the place restaurants and customers find out how to reach us. Same
   layout and styles as the Privacy page. */
const SUPPORT_EMAIL = "submissions@rushmyfood.com";

function Section({ title, children, classes }) {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle2" component="h2" className={classes.boldText} sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function P({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ lineHeight: 1.7, my: 1 }}>
      {children}
    </Typography>
  );
}

function Bullets({ items }) {
  return (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {items.map((item, i) => (
        <li key={i}>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.7 }}>{item}</Typography>
        </li>
      ))}
    </Box>
  );
}

function Support() {
  const classes = useStyle();
  const { isLoggedIn } = useContext(UserContext);

  return (
    <Grid className={classes.root}>
      <Seo
        title="Support"
        description="Get help with RushMyFood and the RushMyFood Restaurant app: email our team about orders, menus, printers, payouts or signing in."
        path="/support"
      />
      {isLoggedIn ? <Header /> : <LoginHeader showIcon />}
      <Grid container className={classes.mainContainer}>
        <Box className={classes.imageContainer}>
          <Typography variant="h4" component="h1" color="textPrimary" align="center" className={classes.title}>
            Support
          </Typography>
        </Box>
        <Grid container item xs={12} className={classes.mainContainer}>
          <Grid item xs={1} md={1} />
          <Grid container item xs={10} sm={10} md={9}>
            <Box>
              <P>
                Need help with RushMyFood or the RushMyFood Restaurant app? Email our team and we'll get back to you.
              </P>

              <Section title="Contact us" classes={classes}>
                <P>
                  Email:{" "}
                  <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover" sx={{ fontWeight: 600 }}>
                    {SUPPORT_EMAIL}
                  </Link>
                </P>
                <P>To help us sort it out quickly, please include:</P>
                <Bullets
                  items={[
                    "Your restaurant's name, or your order number if you're a customer",
                    "The device you're using (iPad, iPhone or Android tablet) and the app version shown at the bottom of the side menu",
                    "What happened, with a screenshot if you can",
                  ]}
                />
              </Section>

              <Section title="For restaurant partners" classes={classes}>
                <Bullets
                  items={[
                    "Signing in: on the sign-in screen, tap Forgot password to reset it, or ask the restaurant owner to send a temporary password from Profile → App Credentials.",
                    "New orders: keep the app open on the Orders screen with the volume up. On iPad and iPhone, the app keeps the screen awake while you're signed in.",
                    "Printers: open Printers in the side menu and tap Add Printer. On iPad and iPhone, pair a Star Bluetooth printer in Settings → Bluetooth first, then scan for it in the app.",
                    "Menu, hours and promotions: change them any time under Menus, Business Hours and Promotions in the side menu.",
                    "Payouts and reports: see sales, commission and your payout under Reports. If a payout looks wrong, email us with the dates.",
                  ]}
                />
              </Section>

              <Section title="Become a partner" classes={classes}>
                <P>
                  Want to sell on RushMyFood?{" "}
                  <Link component={RouterLink} to="/partner-signup" underline="hover" sx={{ fontWeight: 600 }}>
                    Start your partner application
                  </Link>
                  .
                </P>
              </Section>

              <Section title="Customers" classes={classes}>
                <P>
                  Questions about an order you placed? Email us at {SUPPORT_EMAIL} with your order number and the
                  restaurant's name.
                </P>
              </Section>

              <Box sx={{ mt: 4, mb: 6, display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Link component={RouterLink} to="/privacy" underline="hover">Privacy Policy</Link>
                <Link component={RouterLink} to="/terms" underline="hover">Terms</Link>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default React.memo(Support);
